import {DocumentType, FileType} from "./enums";
import {AssociationRead, AssociationReadWReferenceLoad} from "./association";
import {UserRead} from "./user";
import {Group} from "./group";
import {TagRead} from "./tag";
import {TagFilter, PythonDocumentFilter} from "./boolean_filtering";
import {SummarizeTextResponse, Person} from "../routines/schemas";


/**
 * Fields that have Weaviate vector embeddings — determines which columns
 * offer the "Similar To" semantic search option in the filter UI.
 *
 * Mirrors backend WEAVIATE_INDEXED_FIELDS in src/schemas/document.py.
 *
 * - "body" → PARAGRAPH embeddings (primary semantic content)
 * - "summary" and "title" → SUMMARY_INFO embeddings (SummarizeTextResponse.to_string()
 *   includes title, summary, persons, entities, events — so both fields search
 *   the same SUMMARY_INFO vectors)
 */
export const WEAVIATE_INDEXED_FIELDS: ReadonlySet<string> = new Set([
    'body',
    'summary',
    // "title" omitted — SUMMARY_INFO embedding is too diluted to match title-specific queries.
    // Keyword CONTAINS works better for titles.
]);

export function getShorthand(document: DocumentRead): string | undefined {
    const meta = document?.type_specific_metadata;
    const subject = meta && "subject" in meta ? meta.subject : undefined;
    return document?.title || subject || document?.file_name || document?.summary;
}

export type SearchOrigin = "grid" | "grid filtering" | "search" | "tagging" | "nav" | "default";

// Function to convert the date string to a Date object
export function addParsedDateToDocument(document: DocumentRead): DocumentRead {
    const file_modified_date = document.file_modified_date ? new Date(document.file_modified_date) : undefined;
    const file_created_date = document.file_created_date ? new Date(document.file_created_date) : undefined;
    const content_date_start = document.content_date_start ? new Date(document.content_date_start) : undefined;
    const content_date_end = document.content_date_end ? new Date(document.content_date_end) : undefined;
    return {
        ...document,
        file_modified_date_parsed: file_modified_date,
        file_created_date_parsed: file_created_date,
        content_date_start_parsed: content_date_start,
        content_date_end_parsed: content_date_end,
    };
}

export type SortCriteria = {
    field: string;
    direction: "asc" | "desc";
};

export interface DocumentSearch {
    keyword?: string;
    offset?: number;
    limit?: number;
    group_id?: string;
    folder_tag_id?: string;
    submitter_id?: string;
    sort_criteria?: SortCriteria[];
    search_origin?: SearchOrigin;
    tag_filters?: TagFilter[];
    document_filters?: PythonDocumentFilter[];
}

// ------------------
// Email metadata - core fields for legal document management
// ------------------
export type EmailMetadata = {
    from_email?: string;
    to_email?: string;
    subject?: string;
    x_from?: string;
    x_to?: string;
    x_cc?: string;
    x_bcc?: string;
    message_id?: string;
};

// ------------------
// Transcript metadata (mirrors backend TranscriptMetadata)
// NOTE: extracted heuristically from transcript text — every field is optional
// and may be missing when the document doesn't match standard US deposition format.
// ------------------
export type TranscriptAttorney = {
    name: string;
    firm?: string;
    representing?: string;
};

export type ExaminationBlock = {
    examination_type: string;  // DIRECT, CROSS, REDIRECT, RECROSS, VOIR_DIRE, UNKNOWN
    witness?: string;
    attorney?: string;
    transcript_page_start?: number;
    transcript_page_end?: number;
    pdf_page_start?: number;
    pdf_page_end?: number;
    pdf_pages_confident?: boolean;
};

export function getEmailMetadata(doc: DocumentRead): EmailMetadata | undefined {
    const meta = doc.type_specific_metadata;
    if (!meta) return undefined;
    return doc.type === DocumentType.EMAIL ? (meta as EmailMetadata) : undefined;
}

export function getTranscriptMetadata(doc: DocumentRead): TranscriptMetadata | undefined {
    const meta = doc.type_specific_metadata;
    if (!meta) return undefined;
    return doc.type === DocumentType.TRANSCRIPT ? (meta as TranscriptMetadata) : undefined;
}

export type TranscriptMetadata = {
    case_caption?: string;
    case_number?: string;
    court?: string;
    deponent_name?: string;
    deposition_date?: string;
    deposition_location?: string;
    volume?: string;
    court_reporter?: string;
    videographer?: string;
    attorneys?: TranscriptAttorney[];
    examination_index?: ExaminationBlock[];
    transcript_to_pdf_page?: Record<number, number>;
};

// ------------------
// Base document read (no refs added)
// ------------------
export type DocumentRead = {
    id: string;
    type: DocumentType;
    file_type: FileType;

    // ─── Dates ───────────────────────────────────────────────────────────────
    file_created_date?: string;
    file_created_date_parsed?: Date;
    file_modified_date?: string;
    file_modified_date_parsed?: Date;
    content_date_start?: string;
    content_date_start_parsed?: Date;
    content_date_end?: string;
    content_date_end_parsed?: Date;
    content_date_origin?: string;

    // ─── Content ─────────────────────────────────────────────────────────────
    title?: string;
    summary?: string;
    body?: string;
    /** Cached len(body) for budget decisions without loading full body. */
    body_char_count?: number;
    /** Char index in body where each page begins (index i = page i+1). Null for docs without page structure (e.g. emails). */
    page_body_offsets?: number[];
    metadata_string?: string;
    last_embedding?: number[];

    // ✅ Added to match Python
    summary_info?: SummarizeTextResponse;
    participants?: Person[];  // Persons who are active participants (from summary_info.persons)
    participant_names?: string[];  // Names of participants for display/filtering
    upload_job_id?: number;
    is_indexed_in_weaviate?: boolean;

    // ─── References (object refs; keep as you had) ───────────────────────────
    submitter?: UserRead;
    group?: Group;
    folder?: TagRead;  // Folder is now a TagRead with type=FOLDER_UPLOAD

    // ✅ Added scalar ids to mirror Python create/read
    group_id?: string;
    submitter_id?: string;
    folder_tag_id?: string;

    // ✅ Added from Python DocumentRead
    upload_time?: string; // ISO timestamp

    // ─── File Info ───────────────────────────────────────────────────────────
    stored_uri?: string;
    file_name?: string;
    file_extension?: string;
    file_size_in_bytes?: number;
    content_type?: string;

    // ─── Origin Info ─────────────────────────────────────────────────────────
    custodian?: string;
    origin_file_name_full?: string;

    // ─── Metadata ────────────────────────────────────────────────────────────
    type_specific_metadata?: EmailMetadata | TranscriptMetadata;

    // ─── Index Signature ─────────────────────────────────────────────────────
    [key: string]: string | number | boolean | Date | undefined | unknown;
};

// ------------------
// Variants (unchanged shape; just using your types)
// ------------------
export type DocumentReadWRefs = DocumentRead & {
    applied_tag_associations?: AssociationRead[];
};

export type DocumentReadWReferences = DocumentRead & {
    applied_tag_associations?: AssociationReadWReferenceLoad[];
};

// Python: DocumentReadPopulated (body required)
export type DocumentReadPopulated = DocumentRead & {
    body: string;
};

// Python parallels for email
export type EmailRead = DocumentRead & {
    type: DocumentType.EMAIL;
};

export type EmailCreate = DocumentRead & {
    type: DocumentType.EMAIL;
};

// ------------------
// Results wrappers
// ------------------

// You had this:
export type DocumentResults = {
    docs: DocumentRead[];
};

// ✅ Add Python's search results wrapper (non-breaking addition)
export type DocumentSearchResults = {
    docs: DocumentReadWRefs[];
    total: number;
};

// You currently have:
export type DocumentDeleteResults = {
    doc_ids_deletes: string[];
    doc_ids_not_deleted: string[];
};

// ✅ Add a parallel type that matches Python exactly (so you can migrate)
export type DocumentDeleteResultsPy = {
    doc_ids_deleted: string[];
    doc_ids_not_deleted: string[];
};
